/**
 * Host-side lobby UI rendered on the TV. Big room code + QR code, live
 * roster of joined players, and the Start Game button.
 *
 * Mostly read-only with respect to the room — the only mutation issued from
 * here is `startGame` after the host clicks Start. Players toggle their
 * own ready state from `<PlayerLobby />`.
 */

import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconQrcode,
  IconUserMinus,
} from '@tabler/icons-react'
import { QRCodeSVG } from 'qrcode.react'
import { useMemo, useState } from 'react'
import { PLAYER_COLOR_HEX } from '@mdev/common'
import type { PlayerLobbyInfo, Room } from '@mdev/common'
import type { GameModule } from '../module'
import { buildPlayUrl } from './qrcode'
import { kickPlayer, startGame } from './service'

const PLAYER_OFFLINE_MS = 30_000

interface HostLobbyProps {
  room: Room<unknown>
  gameModule: GameModule<unknown, unknown>
  /** Currently always true in HostShell context; reserved for embedded preview. */
  isHost: boolean
}

export function HostLobby( props: HostLobbyProps ) {
  const { room, gameModule, isHost } = props

  const [ starting, setStarting ] = useState( false )
  const [ startError, setStartError ] = useState<string | null>( null )

  // The host is a display, not a player. Iterate ENTRIES so each row carries
  // the Firestore map key as its canonical uid — a partial/corrupt entry can
  // lack its own `uid` field, and we must still be able to kick it by key.
  // Exclude the host; sort by name (tie-break uid) so the list is stable
  // instead of reshuffling as `lastSeenMs` heartbeats land.
  const players = useMemo(
    () =>
      Object.entries( room.players )
        .filter( ( [ uid ] ) => uid !== room.hostUid )
        .map( ( [ uid, info ] ) => ( { uid, info } ) )
        .sort( ( a, b ) => {
          const byName = ( a.info.displayName || '' ).localeCompare( b.info.displayName || '' )
          return byName !== 0 ? byName : a.uid.localeCompare( b.uid )
        } ),
    [ room.players, room.hostUid ],
  )
  const playerCount = players.length

  const allReady = playerCount > 0 && players.every( ( { info } ) => info.ready )
  const meetsMin = playerCount >= gameModule.minPlayers
  const canStart = allReady && meetsMin

  const startReason: string | null = ( () => {
    if ( !meetsMin ) {
      return `Need at least ${ gameModule.minPlayers } player${ gameModule.minPlayers === 1 ? '' : 's' } (have ${ playerCount })`
    }
    if ( !allReady ) {
      const notReady = players.filter( ( { info } ) => !info.ready ).length
      return `Waiting for ${ notReady } player${ notReady === 1 ? '' : 's' } to ready up`
    }
    return null
  } )()

  // QR points to a uid-less route; the player route handles `new` by minting
  // a fresh uid for the device (or reusing the one already in localStorage).
  // Real players each scan from their own phone, so the shared-localStorage
  // identity is per-device as intended.
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const qrUrl = buildPlayUrl( origin, gameModule.id, room.code, 'new' )

  // Open a player in a NEW TAB with a fresh, unique uid. Because PlayerShell
  // trusts the uid in the URL, each call is a distinct player even within the
  // same browser — which `/new` can't do (it reuses one localStorage uid). Use
  // this to simulate several players in tabs on one computer.
  function openTestPlayer() {
    const uid =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `test-${ Date.now() }-${ Math.floor( Math.random() * 1e6 ) }`
    window.open( buildPlayUrl( origin, gameModule.id, room.code, uid ), '_blank', 'noopener' )
  }

  async function onStart() {
    setStartError( null )
    setStarting( true )
    try {
      const initialState = gameModule.setupGame( room )
      // Games with private docs (e.g. SY's Mr. X station) implement
      // `splitInitialState` to bucket the result. For simpler games we
      // fall back to writing the entire TState to `state/main`.
      const docs = gameModule.splitInitialState
        ? gameModule.splitInitialState( initialState )
        : { gameStateDoc: initialState }
      await startGame( room.code, docs )
    } catch ( err ) {
      const message = err instanceof Error ? err.message : 'Failed to start game'
      setStartError( message )
    } finally {
      setStarting( false )
    }
  }

  const HostExtras = gameModule.HostLobbyExtras

  return (
    <Box
      p="xl"
      style={ { minHeight: '100vh', background: '#0B0F1A', color: 'white' } }
    >
      <Group
        align="flex-start"
        justify="space-between"
        wrap="nowrap"
        gap="xl"
      >
        <Stack gap="xs">
          <Text
            size="sm"
            c="dimmed"
            tt="uppercase"
            style={ { letterSpacing: 2 } }
          >
            { gameModule.displayName }
          </Text>
          <Text
            size="sm"
            c="dimmed"
          >
            Room code
          </Text>
          <Title
            order={ 1 }
            style={ {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '8rem',
              lineHeight: 1,
              color: 'white',
              letterSpacing: '0.5rem',
            } }
          >
            { room.code }
          </Title>
        </Stack>

        <Stack
          gap="xs"
          align="center"
        >
          <Anchor
            href={ qrUrl }
            target="_blank"
            rel="noopener"
            underline="never"
            title="Open the join page in a new tab"
          >
            <Paper
              p="md"
              radius="md"
              withBorder
              style={ { background: 'white', cursor: 'pointer' } }
            >
              <QRCodeSVG
                value={ qrUrl }
                size={ 220 }
                level="M"
                includeMargin={ false }
              />
            </Paper>
          </Anchor>

          <Group
            gap={ 6 }
            wrap="nowrap"
            style={ { maxWidth: 252 } }
          >
            <Text
              size="xs"
              c="dimmed"
              style={ {
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              } }
            >
              { qrUrl }
            </Text>
            <CopyButton value={ qrUrl }>
              { ( { copied, copy } ) => (
                <Tooltip
                  label={ copied ? 'Copied' : 'Copy join link' }
                  withArrow
                >
                  <ActionIcon
                    variant="subtle"
                    color={ copied ? 'teal' : 'gray' }
                    onClick={ copy }
                    aria-label="Copy join link"
                  >
                    { copied ? <IconCheck size={ 16 } /> : <IconCopy size={ 16 } /> }
                  </ActionIcon>
                </Tooltip>
              ) }
            </CopyButton>
          </Group>

          <Button
            variant="subtle"
            size="xs"
            color="gray"
            rightSection={ <IconExternalLink size={ 14 } /> }
            onClick={ openTestPlayer }
          >
            Open test player
          </Button>
        </Stack>
      </Group>

      <Stack
        mt="xl"
        gap="md"
      >
        <Group justify="space-between">
          <Title
            order={ 3 }
            c="white"
          >
            Players ({ playerCount }/{ gameModule.maxPlayers })
          </Title>
          <Text
            size="sm"
            c="dimmed"
          >
            Min { gameModule.minPlayers }
          </Text>
        </Group>

        { playerCount === 0 ? (
          <Paper
            p="lg"
            withBorder
            style={ { borderColor: '#1F2937', background: '#0F172A' } }
          >
            <Text c="dimmed">No players joined yet — scan the QR code to join.</Text>
          </Paper>
        ) : (
          <Stack gap="xs">
            { players.map( ( { uid, info } ) => (
              <PlayerRow
                key={ uid }
                uid={ uid }
                player={ info }
                gameId={ gameModule.id }
                code={ room.code }
              />
            ) ) }
          </Stack>
        ) }

        { HostExtras && (
          <Paper
            p="md"
            withBorder
            style={ { borderColor: '#1F2937', background: '#0F172A' } }
          >
            <HostExtras
              room={ room }
              isHost={ isHost }
            />
          </Paper>
        ) }

        <Stack gap="xs">
          <Button
            size="xl"
            color="yellow"
            disabled={ !canStart || starting }
            loading={ starting }
            onClick={ onStart }
          >
            Start Game
          </Button>
          { startReason && (
            <Text
              size="sm"
              c="dimmed"
              ta="center"
            >
              { startReason }
            </Text>
          ) }
          { startError && (
            <Text
              size="sm"
              c="red"
              ta="center"
            >
              { startError }
            </Text>
          ) }
        </Stack>
      </Stack>
    </Box>
  )
}

interface PlayerRowProps {
  /** Canonical id = the Firestore map key (defined even if `player.uid` isn't). */
  uid: string
  player: PlayerLobbyInfo
  gameId: string
  code: string
}

function PlayerRow( props: PlayerRowProps ) {
  const { uid, player, gameId, code } = props
  const [ qrOpen, qrHandlers ] = useDisclosure( false )

  const offline = Date.now() - player.lastSeenMs > PLAYER_OFFLINE_MS
  const swatch = PLAYER_COLOR_HEX[ player.color ]

  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  // Mirrors buildPlayUrl: the games router is mounted at `/games/*`. Use the
  // canonical `uid` (map key), not `player.uid` which may be missing.
  const playerUrl = `${ origin.replace( /\/+$/, '' ) }/games/${ gameId }/play/${ code }/${ uid }`

  return (
    <>
      <Paper
        p="md"
        withBorder
        style={ {
          borderColor: '#1F2937',
          background: '#0F172A',
          opacity: offline ? 0.45 : 1,
          borderLeft: `4px solid ${ swatch }`,
        } }
      >
        <Group justify="space-between">
          <Group gap="md">
            <Text
              style={ { fontSize: '2rem', lineHeight: 1 } }
            >
              { player.avatar }
            </Text>
            <Stack gap={ 2 }>
              <Group gap="xs">
                <Text
                  size="lg"
                  fw={ 600 }
                  c="white"
                >
                  { player.displayName || 'Player' }
                </Text>
                { offline && (
                  <Badge
                    size="sm"
                    color="gray"
                    variant="light"
                  >
                    Reconnecting…
                  </Badge>
                ) }
              </Group>
              <Group gap={ 6 }>
                <Box
                  w={ 12 }
                  h={ 12 }
                  style={ { background: swatch, borderRadius: 999 } }
                />
                <Text
                  size="xs"
                  c="dimmed"
                  tt="capitalize"
                >
                  { player.color }
                </Text>
              </Group>
            </Stack>
          </Group>

          <Group gap="xs">
            { player.ready ? (
              <Badge
                color="green"
                size="lg"
                variant="filled"
                leftSection={ <IconCheck size={ 14 } /> }
              >
                Ready
              </Badge>
            ) : (
              <Badge
                color="yellow"
                size="lg"
                variant="light"
              >
                Not ready
              </Badge>
            ) }
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={ <IconQrcode size={ 14 } /> }
              onClick={ qrHandlers.open }
            >
              Display QR
            </Button>
            <Tooltip
              label="Remove from room"
              withArrow
            >
              <ActionIcon
                size="lg"
                variant="subtle"
                color="red"
                aria-label={ `Remove ${ player.displayName || 'player' }` }
                onClick={ () => {
                  void kickPlayer( code, uid ).catch( () => {} )
                } }
              >
                <IconUserMinus size={ 16 } />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Paper>

      <Modal
        opened={ qrOpen }
        onClose={ qrHandlers.close }
        title={ `Reconnect URL for ${ player.displayName || 'player' }` }
        centered
        size="md"
      >
        <Stack
          align="center"
          gap="md"
        >
          <Paper
            p="md"
            withBorder
            style={ { background: 'white' } }
          >
            <QRCodeSVG
              value={ playerUrl }
              size={ 240 }
              level="M"
              includeMargin={ false }
            />
          </Paper>
          <Text
            size="xs"
            c="dimmed"
            style={ { wordBreak: 'break-all' } }
            ta="center"
          >
            { playerUrl }
          </Text>
        </Stack>
      </Modal>
    </>
  )
}
